import React from 'react';
import { DispatchProp } from 'react-redux';

import {
    Collapsible,
    CollapsibleHeaderContent,
    CollapsibleHeading,
    LightDiceSvg,
} from '@dndbeyond/character-components/es';
import {
    Ability,
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiResponse,
    characterActions,
    CharacterConfiguration,
    CharacterPreferences,
    Constants,
    DefinitionPool,
    FeatDefinitionContract,
    FeatLookup,
    OptionalOriginLookup,
    PrerequisiteData,
    Race,
    RaceUtils,
    RacialTraitUtils,
    rulesEngineSelectors,
    serviceDataSelectors,
} from '@dndbeyond/character-rules-engine/es';
import { ChoiceData } from '@dndbeyond/character-rules-engine/es';
import { Dice } from '@dndbeyond/dice';

import { appEnvActions } from '../../../../Shared/actions/appEnv';
import { apiCreatorSelectors, appEnvSelectors } from '../../../../Shared/selectors';
import { AbilityScoreCalculations } from '../../../components/AbilityScoreCalculations';
import AbilityScoreManagerManual from '../../../components/AbilityScoreManagerManual';
import AbilityScoreManagerPointBuy from '../../../components/AbilityScoreManagerPointBuy';
import AbilityScoreManagerStandardArray from '../../../components/AbilityScoreManagerStandardArray';
import { Button } from '../../../components/Button';
import { Page } from '../../../components/Page';
import { PageBody } from '../../../components/PageBody';
import { PageHeader } from '../../../components/PageHeader';
import RacialTraitList from '../../../components/RacialTraitList';
import { RouteKey } from '../../../constants/navigation';
import { AbilityScoreDiceManager } from '../../AbilityScoreDiceManager';
import AbilityScoreTypeChooser from '../../AbilityScoreTypeChooser';
import ConnectedBuilderPage from '../ConnectedBuilderPage';

interface Props extends DispatchProp {
    abilities: Array<Ability>;
    configuration: CharacterConfiguration;
    diceEnabled: boolean;
    race: Race;
    featLookup: FeatLookup;
    loadAvailableFeats: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<FeatDefinitionContract>>>;
    choiceInfo: ChoiceData;
    prerequisiteData: PrerequisiteData;
    preferences: CharacterPreferences;
    optionalOriginLookup: OptionalOriginLookup;
    definitionPool: DefinitionPool;
}
class AbilityScoresManage extends React.PureComponent<Props> {
    static defaultProps = {
        diceEnabled: false,
    };

    handleScoreChange = (statId: number, value: number | null): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.abilityScoreSet(statId, Constants.AbilityScoreStatTypeEnum.BASE, value));
    };

    handleOverrideScoreChange = (statId: number, value: number | null): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.abilityScoreSet(statId, Constants.AbilityScoreStatTypeEnum.OVERRIDE, value));
    };

    handleOtherBonusChange = (statId: number, value: number | null): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.abilityScoreSet(statId, Constants.AbilityScoreStatTypeEnum.BONUS, value));
    };

    handleDiceEnable = (): void => {
        const { dispatch, diceEnabled } = this.props;

        if (diceEnabled) {
            return;
        }

        const newDiceEnabledSetting: boolean = !diceEnabled;

        try {
            localStorage.setItem('dice-enabled', newDiceEnabledSetting.toString());
            Dice.setEnabled(newDiceEnabledSetting);
        } catch (e) {}

        dispatch(
            appEnvActions.dataSet({
                diceEnabled: newDiceEnabledSetting,
            }),
        );
    };

    renderAbilityScoreManager = (): React.ReactNode => {
        const { abilities, configuration } = this.props;

        switch (configuration.abilityScoreType) {
            case Constants.AbilityScoreTypeEnum.MANUAL:
                return <AbilityScoreManagerManual onChange={this.handleScoreChange} abilities={abilities} />;

            case Constants.AbilityScoreTypeEnum.STANDARD_ARRAY:
                return <AbilityScoreManagerStandardArray onChange={this.handleScoreChange} abilities={abilities} />;

            case Constants.AbilityScoreTypeEnum.POINT_BUY:
                return <AbilityScoreManagerPointBuy onChange={this.handleScoreChange} abilities={abilities} />;

            default:
            // not implemented
        }

        return null;
    };

    renderAbilityDice = (): React.ReactNode => {
        const { diceEnabled } = this.props;

        if (!diceEnabled) {
            return (
                <div className="ability-score-dice-control">
                    <span className="ability-score-dice-control-text">
                        Enable digital dice to assist with rolling your ability scores:
                    </span>
                    <Button size="medium" onClick={this.handleDiceEnable}>
                        <LightDiceSvg />
                        <span className="ability-score-dice-control-button-text">Use Dice</span>
                    </Button>
                </div>
            );
        }

        let headingNode: React.ReactNode = <CollapsibleHeading>Dice Roll Groups</CollapsibleHeading>;

        let headerNode: React.ReactNode = <CollapsibleHeaderContent heading={headingNode} />;

        return (
            <Collapsible header={headerNode} className="ability-score-dice" initiallyCollapsed={false}>
                <AbilityScoreDiceManager />
            </Collapsible>
        );
    };

    handleRacialTraitChoiceChange = (racialTraitId, choiceId, choiceType, optionValue): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.racialTraitChoiceSetRequest(racialTraitId, choiceType, choiceId, optionValue));
    };

    renderRacialTraits() {
        const {
            race,
            featLookup,
            loadAvailableFeats,
            choiceInfo,
            prerequisiteData,
            preferences,
            optionalOriginLookup,
            definitionPool,
        } = this.props;

        if (!race) {
            return null;
        }

        const racialTraits = RaceUtils.getVisibleRacialTraits(race);
        const filteredRacialTraits = RacialTraitUtils.filterRacialTraitsByDisplayConfigurationType(
            racialTraits,
            Constants.DisplayConfigurationTypeEnum.ABILITY_SCORE,
        );

        return (
            filteredRacialTraits.length > 0 && (
                <RacialTraitList
                    racialTraits={filteredRacialTraits}
                    featLookup={featLookup}
                    loadAvailableFeats={loadAvailableFeats}
                    handleRacialTraitChoiceChange={this.handleRacialTraitChoiceChange}
                    choiceInfo={choiceInfo}
                    prerequisiteData={prerequisiteData}
                    preferences={preferences}
                    optionalOriginLookup={optionalOriginLookup}
                    definitionPool={definitionPool}
                    raceName={RaceUtils.getFullName(race)}
                />
            )
        );
    }

    render() {
        const { configuration, abilities } = this.props;

        return (
            <Page>
                <PageBody>
                    <PageHeader>Ability Scores</PageHeader>
                    <AbilityScoreTypeChooser initialOptionRemoved={false} />
                    {this.renderAbilityScoreManager()}
                    {configuration.abilityScoreType === Constants.AbilityScoreTypeEnum.MANUAL &&
                        this.renderAbilityDice()}
                    {this.renderRacialTraits()}
                    <PageHeader>Score Calculations</PageHeader>
                    <p>
                        Calculations, including the base scores you set above and any modifiers, are found below. You
                        can also override any automatic calculations or modify them under each ability summary.
                    </p>
                    <div className="ability-score-calcs">
                        {abilities.map((ability) => (
                            <AbilityScoreCalculations
                                {...ability}
                                key={ability.id}
                                onOverrideScoreChange={this.handleOverrideScoreChange}
                                onOtherBonusChange={this.handleOtherBonusChange}
                            />
                        ))}
                    </div>
                </PageBody>
            </Page>
        );
    }
}

export default ConnectedBuilderPage(AbilityScoresManage, RouteKey.ABILITY_SCORES_MANAGE, (state) => {
    return {
        configuration: rulesEngineSelectors.getCharacterConfiguration(state),
        abilities: rulesEngineSelectors.getAbilities(state),
        diceEnabled: appEnvSelectors.getDiceEnabled(state),
        race: rulesEngineSelectors.getRace(state),
        featLookup: rulesEngineSelectors.getFeatLookup(state),
        loadAvailableFeats: apiCreatorSelectors.makeLoadAvailableFeats(state),
        choiceInfo: rulesEngineSelectors.getChoiceInfo(state),
        prerequisiteData: rulesEngineSelectors.getPrerequisiteData(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        optionalOriginLookup: rulesEngineSelectors.getOptionalOriginLookup(state),
        definitionPool: serviceDataSelectors.getDefinitionPool(state),
    };
});
